import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HiX, HiCheck } from 'react-icons/hi';
import { useAuth } from '../context/AuthContext';
import { createOrder, logWhatsAppMessage, trackEvent } from '../backend/utils/orderUtils';
import { buildOrderMessage, waLink, STORE_WHATSAPP } from '../utils/whatsapp';
/* Aliased: `trackEvent` above writes to Supabase for the admin
   dashboard, these report the same moments to Google Analytics. */
import {
  trackContactClick as gaContactClick,
  trackPurchase as gaPurchase,
} from '../utils/analytics';
import { couponDiscount, meetsMinimum } from '../utils/coupon';
import useScrollLock from '../utils/useScrollLock';
import { saveAddress } from '../services/accountApi';
import {
  STATES,
  composeAddress,
  addressFromProfile,
  addressToColumns,
  validateAddress,
} from '../utils/address';
import '../styles/CheckoutModal.css';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

/**
 * Collects the customer's details and writes the order before opening
 * WhatsApp.
 *
 * Always checks out a cart: <CheckoutModal items={lines} coupon={c} />.
 * It used to take a single `product` too, for the shop's "Buy Now",
 * which skipped the cart entirely — and with it the discount code.
 * That route is gone, so this only ever prices a real basket.
 *
 * Laid out as a sheet: a fixed header, a scrolling middle, and a
 * footer holding the total and the one button that matters. On a phone
 * it fills the screen, so the amount payable and "Place Order" stay in
 * view no matter how far down the address fields you are.
 */
export default function CheckoutModal({ items, coupon = null, onClose, onPlaced }) {
  const { user, profile, refreshProfile } = useAuth();

  const [form, setForm] = useState({
    name: '',
    phone: '',
    house: '',
    street: '',
    landmark: '',
    city: '',
    state: '',
    pincode: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [placed, setPlaced] = useState(null); // { orderNumber, waUrl }

  const [saveForNextTime, setSaveForNextTime] = useState(true);

  // Prefill from the signed-in customer's saved profile + address.
  useEffect(() => {
    if (!profile) return;
    const saved = addressFromProfile(profile);
    setForm((f) => ({
      ...f,
      name: profile.full_name || f.name,
      phone: profile.phone || f.phone,
      house: saved.house || f.house,
      street: saved.street || f.street,
      landmark: saved.landmark || f.landmark,
      city: saved.city || f.city,
      state: saved.state || f.state,
      pincode: saved.pincode || f.pincode,
    }));
  }, [profile]);

  useScrollLock(true);

  // Escape closes, the way every other dialog on the site does.
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const lines = Array.isArray(items) ? items : [];

  const total = lines.reduce(
    (sum, line) => sum + Number(line.product.price || 0) * line.quantity,
    0
  );

  const itemCount = lines.reduce((n, line) => n + line.quantity, 0);

  /* A preview of what the code is worth. The database recomputes it
     when the order is written (order_coupon_trg in
     backend/sql/coupons.sql), so this can never be the number that
     decides what the shop honours. */
  const discount =
    coupon && meetsMinimum(coupon, total) ? couponDiscount(coupon, total) : 0;

  const payable = Math.max(0, total - discount);

  // Never let someone order more than is actually on the shelf.
  const stockOf = (p) =>
    Number.isFinite(Number(p.stock)) ? Number(p.stock) : null;

  /* Returns a message when any line asks for more than is in stock. */
  const stockProblem = () => {
    for (const line of lines) {
      const left = stockOf(line.product);
      if (left === null || line.quantity <= left) continue;

      return left === 0
        ? `"${line.product.name}" just went out of stock. Please remove it to continue.`
        : `Only ${left} of "${line.product.name}" left in stock. Please reduce the quantity.`;
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const digits = form.phone.replace(/\D/g, '');
    if (!form.name.trim()) return setError('Please enter your name.');
    if (digits.length < 10)
      return setError('Please enter a valid WhatsApp number.');
    const addressError = validateAddress(form);
    if (addressError) return setError(addressError);
    if (!lines.length) return setError('Your bag is empty.');

    const outOfStock = stockProblem();
    if (outOfStock) return setError(outOfStock);

    setBusy(true);

    const shippingAddress = composeAddress(form);

    const result = await createOrder({
      customerName: form.name.trim(),
      customerPhone: form.phone.trim(),
      customerEmail: user?.email || null,
      products: lines,
      shippingAddress,
      couponCode: discount > 0 ? coupon.code : null,
      discountAmount: discount,
    });

    if (!result.success) {
      setBusy(false);
      setError(
        `We couldn't save your order: ${result.error}. Please try again, or ` +
          `message us directly on WhatsApp.`
      );
      return;
    }

    const message = buildOrderMessage({
      orderNumber: result.orderNumber,
      customerName: form.name.trim(),
      customerPhone: form.phone.trim(),
      address: shippingAddress,
      items: lines,
      subtotal: total,
      discount,
      couponCode: discount > 0 ? coupon.code : null,
      total: payable,
    });

    const url = waLink(STORE_WHATSAPP, message);

    // Best-effort logging — never block the customer on these.
    logWhatsAppMessage({
      orderId: result.order.id,
      phone: form.phone.trim(),
      messageContent: message,
      messageType: 'order_inquiry',
    }).catch(() => {});

    lines.forEach((line) => {
      trackEvent({
        eventType: 'whatsapp_click',
        productId: line.product.supabaseId,
      }).catch(() => {});
    });

    /* The order row exists now — WhatsApp is only how it gets confirmed,
       so this is the conversion GA should count. */
    gaPurchase({
      orderNumber: result.orderNumber,
      lines,
      value: payable,
      coupon: discount > 0 ? coupon.code : undefined,
    });
    gaContactClick('whatsapp', 'checkout');

    // Remember the address for next time (signed-in customers only).
    if (user && saveForNextTime) {
      saveAddress(user.id, user.email, addressToColumns(form), {
        full_name: form.name.trim(),
        phone: form.phone.trim(),
      })
        .then(() => refreshProfile())
        .catch(() => {});
    }

    setBusy(false);
    setPlaced({ orderNumber: result.orderNumber, waUrl: url });

    // The order is safely in the database — the bag can go.
    onPlaced?.(result);

    window.open(url, '_blank');
  };

  return (
    <div className="checkout-overlay" onClick={onClose}>
      <motion.div
        className="checkout-modal"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Checkout"
      >
        {placed ? (
          /* ---------------------- success ---------------------- */
          <div className="checkout-done">
            <div className="checkout-tick">
              <HiCheck />
            </div>
            <h3>Order Placed</h3>
            <p className="checkout-order-id">{placed.orderNumber}</p>
            <p className="checkout-done-copy">
              We've opened WhatsApp so you can confirm with us. Our team will
              reply with payment and delivery details.
            </p>

            <a
              className="checkout-btn"
              href={placed.waUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open WhatsApp again
            </a>

            {user ? (
              <Link className="checkout-link" to="/account/orders">
                Track this order in My Orders →
              </Link>
            ) : (
              <p className="checkout-hint">
                <Link to="/account/login">Create an account</Link> with this
                phone number to track your orders.
              </p>
            )}

            <button className="checkout-ghost" onClick={onClose}>
              Continue shopping
            </button>
          </div>
        ) : (
          /* ------------------------ form ----------------------- */
          <>
            <header className="checkout-head">
              <div>
                <h3>Confirm Your Order</h3>
                <p>
                  {itemCount} {itemCount === 1 ? 'item' : 'items'} · pay after
                  we confirm on WhatsApp
                </p>
              </div>
              <button
                className="checkout-close"
                onClick={onClose}
                aria-label="Close"
                type="button"
              >
                <HiX />
              </button>
            </header>

            <form onSubmit={handleSubmit} className="checkout-body" id="checkout-form">
              {/* ---------- what's being bought ---------- */}
              <section className="checkout-card">
                <div className="checkout-lines">
                  {lines.map((line) => (
                    <div
                      className="checkout-line"
                      key={line.product.supabaseId || line.product.id}
                    >
                      {line.product.image ? (
                        <img src={line.product.image} alt={line.product.name} />
                      ) : (
                        <div className="checkout-line-fallback">
                          {line.product.emoji || '💎'}
                        </div>
                      )}
                      <div className="checkout-line-info">
                        <strong>{line.product.name}</strong>
                        <span className="checkout-sub">
                          {line.product.emoji} {line.product.planet} ×{' '}
                          {line.quantity}
                        </span>
                      </div>
                      <span className="checkout-price">
                        {inr(line.product.price * line.quantity)}
                      </span>
                    </div>
                  ))}
                </div>

                <dl className="checkout-sums">
                  <div>
                    <dt>Subtotal</dt>
                    <dd>{inr(total)}</dd>
                  </div>
                  {discount > 0 && (
                    <div className="is-discount">
                      <dt>Discount ({coupon.code})</dt>
                      <dd>−{inr(discount)}</dd>
                    </div>
                  )}
                  <div>
                    <dt>Shipping</dt>
                    <dd className="is-free">Free</dd>
                  </div>
                </dl>
              </section>

              {/* ---------- who it's going to ---------- */}
              <section className="checkout-section">
                <h4>Contact Details</h4>

                <label className="checkout-field">
                  <span>Your name</span>
                  <input
                    value={form.name}
                    onChange={(e) => set('name', e.target.value)}
                    placeholder="Full name"
                    autoComplete="name"
                  />
                </label>

                <label className="checkout-field">
                  <span>WhatsApp number</span>
                  <input
                    value={form.phone}
                    onChange={(e) => set('phone', e.target.value)}
                    placeholder="+91 99999 00000"
                    inputMode="tel"
                    autoComplete="tel"
                  />
                </label>
              </section>

              <section className="checkout-section">
                <h4>Delivery Address</h4>

                <label className="checkout-field">
                  <span>Flat / house no, building</span>
                  <input
                    value={form.house}
                    onChange={(e) => set('house', e.target.value)}
                    placeholder="e.g. B-402, Shanti Apartments"
                    autoComplete="address-line1"
                  />
                </label>

                <label className="checkout-field">
                  <span>Street / area / locality</span>
                  <input
                    value={form.street}
                    onChange={(e) => set('street', e.target.value)}
                    placeholder="e.g. Green Park Extension"
                    autoComplete="address-line2"
                  />
                </label>

                <label className="checkout-field">
                  <span>
                    Landmark <em>optional</em>
                  </span>
                  <input
                    value={form.landmark}
                    onChange={(e) => set('landmark', e.target.value)}
                    placeholder="e.g. Opposite Metro Station"
                  />
                </label>

                <div className="checkout-row">
                  <label className="checkout-field">
                    <span>City</span>
                    <input
                      value={form.city}
                      onChange={(e) => set('city', e.target.value)}
                      placeholder="New Delhi"
                      autoComplete="address-level2"
                    />
                  </label>

                  <label className="checkout-field">
                    <span>PIN code</span>
                    <input
                      value={form.pincode}
                      onChange={(e) =>
                        set('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))
                      }
                      placeholder="110016"
                      inputMode="numeric"
                      autoComplete="postal-code"
                    />
                  </label>
                </div>

                <label className="checkout-field">
                  <span>State</span>
                  <select
                    value={form.state}
                    onChange={(e) => set('state', e.target.value)}
                    autoComplete="address-level1"
                  >
                    <option value="">Select state</option>
                    {STATES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </label>

                {user ? (
                  <label className="checkout-check">
                    <input
                      type="checkbox"
                      checked={saveForNextTime}
                      onChange={(e) => setSaveForNextTime(e.target.checked)}
                    />
                    <span>Save these details for next time</span>
                  </label>
                ) : (
                  <p className="checkout-hint">
                    <Link to="/account/login">Sign in</Link> to save your details
                    and track this order later.
                  </p>
                )}
              </section>
            </form>

            {/* The total and the action stay put while the form scrolls. */}
            <footer className="checkout-foot">
              {error && <p className="checkout-error">{error}</p>}

              <div className="checkout-total">
                <span>Total</span>
                <strong>{inr(payable)}</strong>
              </div>

              <button
                type="submit"
                form="checkout-form"
                className="checkout-btn"
                disabled={busy}
              >
                {busy ? 'Placing order…' : 'Place Order via WhatsApp'}
              </button>
            </footer>
          </>
        )}
      </motion.div>
    </div>
  );
}
